import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Event } from 'src/app/model/event';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { SelectOption } from 'src/app/model/select.option';
import { AdminService } from 'src/app/service/admin.service';
import { StatisticsService, AuthorCountries } from 'src/app/service/statistics.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Dictionary } from 'src/app/interface/idictionary';
import { ExportService } from 'src/app/service/export.service';

let unknownCountriesLabel : String;
@Component({
  selector: 'app-submission-author-statistics',
  templateUrl: './submission-author-statistics.component.html',
  styleUrls: ['./submission-author-statistics.component.scss']
})

export class SubmissionAuthorStatisticsComponent implements OnInit, OnDestroy {
  @Input() event: Event;

  form: FormGroup;
  subscriptions: Subscription[] = [];
  haveSubmissions: boolean = false;

  filterControl = new FormControl('');
  tracks: Array<SelectOption>;
  submissionGroups: Array<SelectOption>;
  options: any;
  
  authorsCountry : Array<any>;
  dsAuthorsCountry : Array<any>;


  tableColumns = ['country', 'pending', 'active', 'withdrawn', 'accepted', 'rejected', 'total']

  // Flag that determines whether the unknown countries are considered in the data
  hideUnknown: boolean =false;

  // The number of items before filtering
  originalAuthorsCountrySize: Number =0;
  showSubEventsTracks :boolean = false;


  filterParams : Record<any,any>= {}
  constructor(
    private statisticsService: StatisticsService,
    protected adminService: AdminService,
    public fb: FormBuilder,
    public translateService: TranslateService,
    private exportService: ExportService
  ) { }

  ngOnInit() {
    // Get the label of the unknown countries (just once at start)
    unknownCountriesLabel = this.translateService.instant('dataset-options.unknownCountryLabel')
    setTimeout(() => {
      this.adminService.progressBar.start();
      if (!this.event) {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
        }, () => {
        }, () => {
          this.initComponent();
          this.getDataAuthorsCountryBySubmission(true);
        });
      } else {
        this.initComponent();
        this.getDataAuthorsCountryBySubmission(true);
      }
      this.setFilterChangeSubscription();
    });
  }

  initComponent(){
    this.form = this.fb.group({
      tracks: [],
      submissionGroups: [],
    });

    this.tracks = this.event.tracks.map(t => {
      return new SelectOption(t.id, t.name);
    });

    this.adminService.eventsService.getEventPaperGroups(this.event.id).subscribe(paperGroups => {
      this.submissionGroups = paperGroups.map(g => 
        new SelectOption(g.id, g.name)
      );

      if (this.submissionGroups.length === 0) {
        this.form.controls['submissionGroups'].disable();
      }
    });
  }

  getDataAuthorsCountryBySubmission(init: boolean){
    this.adminService.progressBar.start();
    this.statisticsService.getAuthorsCountry(this.filterParams, this.event.id).subscribe( datasource => {
      this.authorsCountry = datasource.items;
      this.authorsCountry.map(country =>  {
        // Handle the null country names
        if(!country.country){
          country.country = unknownCountriesLabel
        }
      })
      this.dsAuthorsCountry = this.authorsCountry;
      this.statisticsService.setCountriesStatus(this.dsAuthorsCountry)
      this.setTotalSum();
    }, () => {
      this.adminService.progressBar.stop();
    },() => {
      if (init) {
        if (this.authorsCountry.length > 0 ){
          this.originalAuthorsCountrySize = this.authorsCountry.length
          this.haveSubmissions = true;
        } else {
          this.haveSubmissions = false;
        }
      }
      this.adminService.progressBar.stop();
    });
  }

  hideOrShowUnknown(){
    if(this.hideUnknown){
      this.dsAuthorsCountry = this.authorsCountry.filter(item => item.country !== unknownCountriesLabel);
    }else{
      // Use the last filter parameters to get data back  (before filtering to remove unknown)
      this.dsAuthorsCountry = this.authorsCountry;
    }
    this.statisticsService.setCountriesStatus(this.dsAuthorsCountry.filter(t => t.track !== 'Total'))// Pass data to the shared service (without the total row)
  }

  setFilterChangeSubscription() {
    this.subscriptions.push(
      this.form.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged()
        )
        .subscribe( value => {
          // Create the parameters for the query
          this.filterParams = {}
          if (value.tracks && value.tracks.length > 0) {
            this.filterParams['tracks'] = value.tracks
          }
          if (value.submissionGroups && value.submissionGroups.length > 0) {
            this.filterParams['submission_groups'] = value.submissionGroups
          }
          this.filterParams['count_subevents']=this.showSubEventsTracks;
          // Every time the filter changes, the query is made again
          this.getDataAuthorsCountryBySubmission(false);
        }
      )
    );
  }

  private hideOrShowSubTracks(): void{
    this.filterParams['count_subevents']=this.showSubEventsTracks;
    // Every time the filter changes, the query is made again
    this.getDataAuthorsCountryBySubmission(false);
  }

  private setTotalSum(): void{
    const totalIndex = this.authorsCountry.findIndex(t => t.track === 'Total');

    const totals = this.authorsCountry.reduce((acc, curr) => {
      if (curr.country === 'Total') return acc; // Skip existing total if present
      acc.pending += curr.pending;
      acc.active += curr.active;
      acc.withdrawn += curr.withdrawn;
      acc.accepted += curr.accepted;
      acc.rejected += curr.rejected;
      acc.total += curr.total;
      return acc;
    }, {
      country: 'Total',
      pending: 0,
      active: 0,
      withdrawn: 0,
      accepted: 0,
      rejected: 0,
      total: 0
    });

    if (totalIndex !== -1) {
      // Update the existing total
      this.authorsCountry[totalIndex] = totals;
    } else {
      // Add the total entry
      this.authorsCountry.push(totals);
    }
  }

  private downloadReport(): void{
    let topicsCSV = this.tableColumns.join(';')+'\n';
    topicsCSV += this.authorsCountry.map(m => `${m.country};${m.pending};${m.active};${m.withdrawn};${m.accepted};${m.rejected};${m.total}`).join('\n');
    this.exportService.downloadFile(`${this.event.name}-authors-statistics.csv`, topicsCSV, 'text/csv');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
